import { Component, computed, effect, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ThemeService } from '../services/theme.service';
import { ThemeSourcePipe } from '../shared/pipes/theme-source.pipe';
import { Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { DataService } from '../services/data.service';
import { TranslateService } from '@ngx-translate/core';
import { PayAtTablePaymentService } from '../services/pay-at-table-payment.service';
import { TableSocketService } from '../services/table-socket.service';
import { Restaurant } from '../models/config.model';
import { ApiService } from '../services/api.service';
import { PaymentMode, MenuAction, NotificationType } from '../shared/enums';
import { DomSanitizer } from '@angular/platform-browser';
import { NotificationPayload } from '../models/notification.model';
import { isChequeEvent } from '../models/websocket.model';
import { WaiterComponent } from '../shared/waiter/waiter.component';
import { ToastService } from '../services/toast.service';
import { SHOW_MENU_HOME_STATE } from '../menu/menu-home.guard';

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
  standalone: false
})

export class LandingComponent implements OnInit, OnDestroy {

  currentRestaurant: Restaurant | undefined;
  wallpaperUrl: string | null = null;
  // Read live, not copied in ngOnInit: picking a language refetches the config, and
  // on a first-visit pick that response (with server-translated labels) lands after
  // this page has already initialised.
  readonly waiterOptions = computed(() => this.dataService.filteredWaiterOptions() ?? []);
  get ctaText(): string | null | undefined { return this.dataService.ctaText; }
  get ctaUrl(): string | null | undefined { return this.dataService.ctaUrl; }
  orderAmount = 0;
  isLoadingBill = true;
  payAtTableMode = PaymentMode.PayAtTable;
  isCallWaiterEnabled = false;
  waiterOptionCountdowns = new Map<string, number>();
  private intervalId: any;
  logoUrl = computed(() => {
    const url = this.dataService.restaurant().logo_url;
    return url
      ? this.sanitizer.bypassSecurityTrustStyle(`url('${url}')`)
      : null;
  });

  // Subscriptions
  private chequeUpdateSubscription = Subscription.EMPTY;

  constructor(
    private router: Router,
    private themeService: ThemeService,
    private themeSourcePipe: ThemeSourcePipe,
    private dataService: DataService,
    private apiService: ApiService,
    private bottomSheet: MatBottomSheet,
    private toastService: ToastService,
    private translateService: TranslateService,
    private paymentService: PayAtTablePaymentService,
    private sanitizer: DomSanitizer,
    private tableSocketService: TableSocketService,
  ) {
    effect(() => {
      this.wallpaperUrl = this.themeService.landingWallpaper();
    });
  }

  ngOnInit(): void {
    this.currentRestaurant = this.dataService.restaurant();

    if (this.currentRestaurant && !this.dataService.hasLandingPage) {
      this.router.navigate(['/menu-home']);
      return;
    }

    this.isCallWaiterEnabled = this.checkCallWaiterEnabled();
    if (this.isCallWaiterEnabled) {
      this.startCountdownLoop();
    }

    if (this.currentRestaurant?.payment_mode === PaymentMode.PayAtTable) {
      this.calculateOrderAmount();

      // Keeps the displayed bill total in sync with other guests' orders/payments on this table.
      const tableId = this.dataService.table()?.id;
      this.chequeUpdateSubscription = this.tableSocketService.message$.pipe(
        filter((event) => isChequeEvent(event) && event.table_id === tableId),
        debounceTime(300),
      ).subscribe(() => this.calculateOrderAmount());
    }
  }

  ngOnDestroy(): void {
    this.chequeUpdateSubscription.unsubscribe();
    clearInterval(this.intervalId);
  }

  displayMenu() {
    this.router.navigate(['menu-home'], { state: SHOW_MENU_HOME_STATE });
  }

  checkCallWaiterEnabled(): boolean {
    return this.dataService.menuActions()
      .some(action => action.action === MenuAction.Waiter);
  }

  callWaiter(optionId?: string) {
    if (!optionId) {
      // Handle the case where no specific waiter option is selected
      this.bottomSheet.open(WaiterComponent);
    } else {
      if (this.dataService.isWaiterOptionBlocked(optionId)) return;
      this.dataService.setWaiterOptionBlocked(optionId);
      const seconds = this.dataService.getWaiterOptionRemainingTime(optionId);
      this.waiterOptionCountdowns.set(optionId, seconds);

      const notification: NotificationPayload = this.makeWaiterNotification(optionId);
      this.apiService.sendNotification(notification).subscribe(() => {
        this.toastService.success(this.translateService.instant('WAITER.SNACKBAR_TITLE'));
      });
    }
  }

  makeWaiterNotification(optionId: string): NotificationPayload {
    return {
      type: NotificationType.CallWaiter,
      table: this.dataService.table().id,
      user: this.dataService.getUserId(),
      waiter_option_id: optionId,
      comment: '',
    };
  }

  // Waiter Option Countdowns
  startCountdownLoop(): void {
    this.updateCountdowns();

    this.intervalId = setInterval(() => {
      this.updateCountdowns();
    }, 1000);
  }

  updateCountdowns(): void {
    for (const option of this.waiterOptions()) {
      const seconds = this.dataService.getWaiterOptionRemainingTime(option.id);
      this.waiterOptionCountdowns.set(option.id, seconds);
    }
  }


  calculateOrderAmount() {
    this.paymentService.applyPostpaidCheckoutThenLoadOrders().subscribe({
      next: (rsp) => {
        this.orderAmount = Number(rsp?.cheque.amount_to_pay ?? 0);
        this.isLoadingBill = false;
      },
      error: (err) => {
        this.isLoadingBill = false;
      }
    });
  }

  displayCheckout() {
    this.paymentService.setOrigin(this.router.url);
    this.router.navigate(['/pay-at-table']);
  }

  navigateToCTA() {
    if (this.ctaUrl) {
      window.open(this.ctaUrl, "_blank");
    }
  }
}
